'use client';

import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';

/**
 * Най-късният момент за монтиране, ако браузърът така и не намери свободна пауза.
 * `requestIdleCallback` приема timeout и сам го гарантира; `setTimeout` е
 * резервата за браузъри без rIC (Safari под 16.4).
 */
export const IDLE_TIMEOUT_MS = 2000;

/**
 * Двата widget-а се рендерират в root layout-а, но нито един не е видим при
 * зареждане: бутонът за проблем е плаващ, а промо попъпът има собствено
 * изчакване (`showDelayMs`, по подразбиране 10 s, admin-контролирано).
 *
 * Въпреки това JS-ът и CSS-ът им влизаха в критичния път. Измерено на
 * продукция (2026-08-29): 18.5 KB JS в layout chunk-а и 13.5 KB в inline CSS-а
 * на всяка страница — за нещо, което не се показва.
 *
 * `ssr: false` е коректно тук: и двата зависят от клиентско състояние
 * (localStorage cooldown при промото, `useTurnstile` при формата) и на сървъра
 * така или иначе не рендерират нищо.
 *
 * Компромис: собственото изчакване на промото стартира след монтирането, тоест
 * попъпът се появява до ~2 s по-късно от преди. При праг 10 s това е в рамките
 * на допустимото; прагът е admin-контролиран и може да се смъкне.
 */
const ReportIssueWidget = dynamic(() => import('./feedback/ReportIssueWidget'), { ssr: false });
const PromoSubscribePopup = dynamic(() => import('./promo/PromoSubscribePopup'), { ssr: false });
// Т.8: само за логнати с Google без съгласие; промо попъпът е само за гости → не се засичат.
const MarketingConsentPrompt = dynamic(() => import('./marketing/MarketingConsentPrompt'), {
  ssr: false,
});

export default function DeferredWidgets() {
  const [mount, setMount] = useState(false);

  useEffect(() => {
    let idleId = null;
    let timerId = null;

    const start = () => setMount(true);

    if (typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(start, { timeout: IDLE_TIMEOUT_MS });
    } else {
      timerId = window.setTimeout(start, IDLE_TIMEOUT_MS);
    }

    return () => {
      if (idleId != null && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
      if (timerId != null) {
        window.clearTimeout(timerId);
      }
    };
  }, []);

  if (!mount) {
    return null;
  }

  return (
    <>
      <ReportIssueWidget />
      <PromoSubscribePopup />
      <MarketingConsentPrompt />
    </>
  );
}
